import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hrRequestApi } from '../../api/hr-requests';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const TYPE_LABELS: Record<string, string> = {
  confirmation_letter: 'Employment Confirmation Letter', salary_confirmation: 'Salary Confirmation',
  id_card_replacement: 'ID Card Replacement', contract_amendment: 'Contract Amendment',
  profile_update: 'Profile Update', training: 'Training', other: 'Other',
};
const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft', approved: 'Approved', rejected: 'Rejected', completed: 'Completed', closed: 'Closed',
};

export function MyRequestsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const requestsQuery = useQuery({ queryKey: ['my-hr-requests'], queryFn: () => hrRequestApi.mine() });
  const { register, handleSubmit, reset } = useForm<{ requestType: string; details?: string }>();

  const createMutation = useMutation({
    mutationFn: (values: { requestType: string; details?: string }) => hrRequestApi.create(values),
    onSuccess: () => { showSuccess('Request submitted.'); queryClient.invalidateQueries({ queryKey: ['my-hr-requests'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit request.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <PageHeader title="My HR Requests" />

      <section className="card">
        <h2>New Request</h2>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Type
            <select {...register('requestType', { required: true })}>
              {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Details<input {...register('details')} /></label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Submitting…' : 'Submit'}</button>
        </form>
      </section>

      <div className="panel">
        <SectionHeader title="My Requests" />
        <div className="panel-body">
        {requestsQuery.data && (
          <DataTable
            rows={requestsQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No requests yet."
            columns={[
              { header: 'Type', render: (r) => TYPE_LABELS[r.request_type] ?? r.request_type },
              { header: 'Details', render: (r) => r.details ?? '-' },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
            ]}
          />
        )}
        {requestsQuery.data?.data.filter((r) => r.status === 'returned').map((r) => (
          <ReturnedBanner key={r.id} returnComment={r.return_comment} resubmit={() => hrRequestApi.resubmit(r.id)} queryKeysToInvalidate={[['my-hr-requests']]} />
        ))}
        </div>
      </div>
    </div>
  );
}
